import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface ButtonProps {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  disabled,
  type = 'button',
  onClick,
}) => {
  const baseStyles = `
    relative inline-flex items-center justify-center gap-2
    font-semibold rounded-xl
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--bg-primary)]
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    overflow-hidden
  `;

  const variants = {
    primary: `
      bg-gradient-to-r from-primary-600 to-accent-500
      text-white
      hover:from-primary-500 hover:to-accent-400
      hover:shadow-[0_0_30px_rgba(124,58,237,0.4)]
      hover:-translate-y-0.5
      active:translate-y-0
      focus:ring-primary-500
    `,
    secondary: `
      bg-[var(--bg-tertiary)]
      text-[var(--text-primary)]
      border border-[var(--border-color)]
      hover:bg-[var(--bg-card-hover)]
      hover:border-[var(--border-color-hover)]
      hover:-translate-y-0.5
      active:translate-y-0
      focus:ring-primary-500
    `,
    ghost: `
      bg-transparent
      text-[var(--text-secondary)]
      hover:bg-[var(--bg-card)]
      hover:text-[var(--text-primary)]
      focus:ring-primary-500
    `,
    danger: `
      bg-gradient-to-r from-error-600 to-error-500
      text-white
      hover:from-error-500 hover:to-error-400
      hover:shadow-[0_0_30px_rgba(239,68,68,0.4)]
      hover:-translate-y-0.5
      active:translate-y-0
      focus:ring-error-500
    `,
    success: `
      bg-gradient-to-r from-success-600 to-accent-500
      text-white
      hover:from-success-500 hover:to-accent-400
      hover:shadow-[0_0_30px_rgba(34,197,94,0.4)]
      hover:-translate-y-0.5
      active:translate-y-0
      focus:ring-success-500
    `,
    outline: `
      bg-transparent
      text-[var(--text-primary)]
      border border-[var(--border-color)]
      hover:bg-[var(--bg-tertiary)]
      hover:border-[var(--border-color-hover)]
      hover:-translate-y-0.5
      active:translate-y-0
      focus:ring-primary-500
    `,
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-7 py-3.5 text-base',
  };

  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.97 }}
      className={clsx(
        baseStyles,
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled || isLoading}
      type={type}
      onClick={onClick}
    >
      {/* Shimmer effect on hover */}
      <span className="absolute inset-0 overflow-hidden rounded-xl">
        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:animate-shimmer" />
      </span>

      {isLoading ? (
        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          <span className="relative z-10">{children}</span>
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </>
      )}
    </motion.button>
  );
};

export default Button;
